import React, { useEffect, useRef, useState } from "react";
import "./MainRobotHead.css";
import { attachAnalyser } from "@/lib/audioGraph";

/**
 * MainRobotHead — the master JARVIS character.
 *
 * Owner brief: "main robot looks exactly like that retro TV-head circuit-
 * board guy in screenshot 4 — square CRT monitor head with pixel-grid
 * eyes and a mouth that moves with the words, body made of glowing
 * circuit-board lines." Replaces the previous cartoon SVG TalkingHead
 * for the main brain voice (palette: cyan).
 *
 * Audio reactivity: when `audioEl` is provided we wire an AnalyserNode
 * onto it and drive the mouth pixel-row count off live volume. Drops
 * gracefully back to a static smile if no audio is supplied.
 *
 * Per design_guidelines.json -> components.MainRobotHead.
 */
export default function MainRobotHead({ audioEl, speaking = false, size = 220 }) {
  const [mouthLevel, setMouthLevel] = useState(0);  // 0..1
  const rafRef = useRef(null);
  const analyserRef = useRef(null);

  useEffect(() => {
    if (!audioEl || !speaking) {
      setMouthLevel(0);
      return undefined;
    }
    let cancelled = false;
    try {
      const analyser = attachAnalyser(audioEl, { fftSize: 64 });
      if (!analyser) throw new Error("analyser unavailable");
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (cancelled) return;
        analyser.getByteFrequencyData(data);
        // Low-mid frequency band (roughly speech range) — average bins 2..10.
        let sum = 0, n = 0;
        for (let i = 2; i < Math.min(10, data.length); i++) { sum += data[i]; n++; }
        const avg = n ? sum / n : 0;
        const norm = Math.min(1, avg / 140);  // 140 ≈ comfortable speech peak
        setMouthLevel(norm);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      // Fallback: faux mouth move from a slow oscillator so the head
      // doesn't look frozen if the audio pipeline is locked.
      console.warn("[MainRobotHead] analyser failed:", e?.message);
      let t = 0;
      const tick = () => {
        if (cancelled) return;
        t += 0.18;
        setMouthLevel((Math.sin(t) + 1) / 2 * 0.6);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [audioEl, speaking]);

  // Mouth pixel grid — 6x3. The number of lit rows tracks mouthLevel.
  const litRows = Math.max(1, Math.round(mouthLevel * 3));
  const mouthRows = [0, 1, 2];

  return (
    <div className="main-robot-head" data-testid="main-robot-jarvis" style={{ width: size }}>
      {/* Body — circuit-board silhouette below the screen */}
      <svg className="robot-body" viewBox="0 0 200 140" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="bodyGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0a2a3a" />
            <stop offset="100%" stopColor="#02060a" />
          </linearGradient>
        </defs>
        {/* Hoodie silhouette */}
        <path d="M40 0 Q100 -10 160 0 L180 60 L160 140 L40 140 L20 60 Z" fill="url(#bodyGrad)" stroke="#00f0ff44" strokeWidth="0.6" />
        {/* Circuit-board traces */}
        {[...Array(8)].map((_, i) => (
          <g key={i} opacity={0.7}>
            <line x1={30 + i * 18} y1="20" x2={30 + i * 18} y2="60" stroke="#00f0ff" strokeWidth="0.4" />
            <circle cx={30 + i * 18} cy={62 + (i % 2) * 14} r="1.2" fill="#00f0ff" />
          </g>
        ))}
        {[...Array(5)].map((_, i) => (
          <line key={`h${i}`} x1="30" y1={70 + i * 12} x2="170" y2={70 + i * 12} stroke="#00f0ff" strokeWidth="0.3" opacity={0.55} />
        ))}
      </svg>

      {/* CRT head */}
      <div className="robot-crt">
        <div className="robot-crt-screen">
          {/* Pixel-grid eyes */}
          <div className="robot-eye-row">
            <div className="robot-eye">
              <div className="robot-eye-dot" />
              <div className="robot-eye-dot" />
              <div className="robot-eye-dot" />
              <div className="robot-eye-dot" />
            </div>
            <div className="robot-eye">
              <div className="robot-eye-dot" />
              <div className="robot-eye-dot" />
              <div className="robot-eye-dot" />
              <div className="robot-eye-dot" />
            </div>
          </div>
          {/* Audio-reactive mouth pixel grid */}
          <div className="robot-mouth">
            {mouthRows.map((row) => (
              <div key={row} className={`robot-mouth-row ${row < litRows ? "lit" : ""}`}>
                {[0, 1, 2, 3, 4, 5].map((c) => (
                  <span key={c} className="robot-mouth-dot" />
                ))}
              </div>
            ))}
          </div>
          {/* Scanline overlay */}
          <div className="robot-scanlines" />
        </div>
        {/* Antenna */}
        <div className="robot-antenna" />
      </div>
    </div>
  );
}
